import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Warning, ArrowRight, CreditCard, Bank, FileText, Globe, ArrowsClockwise } from "@phosphor-icons/react";
import { useT } from "../lib/i18n";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

/**
 * /receive/:token
 *
 * Unified receiver onboarding hub. Replaces the three separate links
 * (cardholder terms, VietQR setup, payout onboarding) with one
 * bookmarkable URL that shows a 3-step checklist. Each step links to
 * the existing sub-flow with a freshly-minted child token.
 *
 * Auto-refreshes status on tab-focus so when the receiver completes a
 * sub-flow and returns, the hub updates without manual reload.
 */

const ICONS = {
  terms: FileText,
  vietqr: Bank,
  payout: CreditCard,
};

function StepCard({ step }) {
  const t = useT();
  const Icon = ICONS[step.key] || Globe;
  const isExternal = step.setup_url && !step.setup_url.startsWith("/");
  return (
    <li
      className={`rounded-2xl border-2 p-5 transition-all ${
        step.done
          ? "border-emerald-300 bg-emerald-50/40"
          : step.required
            ? "border-amber-300 bg-amber-50/30"
            : "border-[#E5E0D8] bg-white"
      }`}
      data-testid={`hub-step-${step.key}`}
    >
      <div className="flex items-start gap-4">
        <span
          className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
            step.done ? "bg-emerald-600 text-white" : "bg-[#184A30]/10 text-[#184A30]"
          }`}
        >
          {step.done ? <CheckCircle size={22} weight="fill" /> : <Icon size={22} weight="duotone" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-[#112318]">{step.title}</p>
            {step.required && !step.done && (
              <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                {t("hub.required_pill")}
              </span>
            )}
            {step.done && (
              <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                {t("hub.done_pill")}
              </span>
            )}
          </div>
          <p className="text-xs text-[#4A5C50] mt-1">{step.description}</p>
          {step.done && step.done_detail && (
            <p className="text-xs text-emerald-700 mt-2 mono" data-testid={`hub-step-${step.key}-detail`}>
              {step.done_detail}
            </p>
          )}
          {!step.done && step.setup_url && (
            <Link
              to={isExternal ? "#" : step.setup_url}
              onClick={(e) => {
                if (isExternal) {
                  e.preventDefault();
                  window.location.href = step.setup_url;
                }
              }}
              className="inline-flex items-center gap-1 text-[#184A30] text-sm font-semibold mt-3 hover:underline"
              data-testid={`hub-step-${step.key}-setup`}
            >
              {t("hub.setup_btn")} <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </li>
  );
}

export default function ReceiveHub() {
  const { token } = useParams();
  const t = useT();
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const r = await axios.get(`${API_BASE}/api/public/receive/${token}`);
      setInfo(r.data);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.response?.data?.detail || t("hub.error.default"));
    } finally {
      setRefreshing(false);
    }
  }, [token, t]);

  useEffect(() => { load(true); }, [load]);

  // Refresh when the tab regains focus — captures completion of sub-flows.
  useEffect(() => {
    const onFocus = () => load(true);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") load(true);
    });
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-rose-200 p-6 text-center" data-testid="hub-error">
          <Warning size={42} weight="duotone" className="mx-auto text-rose-500" />
          <h1 className="serif text-2xl text-[#112318] mt-3">{t("hub.error.title")}</h1>
          <p className="text-sm text-[#4A5C50] mt-2">{loadError}</p>
          <Link to="/" className="inline-block mt-4 text-sm text-[#184A30] underline">{t("hub.back_home")}</Link>
        </div>
      </div>
    );
  }

  if (!info) {
    return <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center"><p className="text-[#4A5C50] text-sm">{t("hub.loading")}</p></div>;
  }

  const allDone = info.summary.all_required_done && info.summary.done === info.summary.total;
  const pct = Math.round((info.summary.done / info.summary.total) * 100);
  const firstName = info.receiver_name?.split(" ")[0] || t("hub.fallback_name");
  const blockerCount = info.summary.required_blockers;

  return (
    <div className="min-h-screen bg-[#FBF9F6] py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <img src="/zcardon-logo.png" alt="Cardon" className="h-8" onError={(e) => (e.target.style.display = "none")} />
          <button
            onClick={() => load(false)}
            disabled={refreshing}
            className="text-xs text-[#4A5C50] hover:text-[#112318] inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="hub-refresh-btn"
          >
            <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} /> {t("hub.refresh")}
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E0D8] shadow-sm overflow-hidden">
          <div className="px-6 pt-6 pb-5 border-b border-[#E5E0D8]">
            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">
              <Globe size={12} /> {t("hub.eyebrow")}
            </span>
            <h1 className="serif text-3xl text-[#112318] mt-2" data-testid="hub-heading">
              {allDone ? t("hub.heading.all_done") : t("hub.heading.greet", { name: firstName })}
            </h1>
            <p className="text-sm text-[#4A5C50] mt-2 leading-relaxed">
              {info.sender_first_name
                ? <><b>{info.sender_first_name}</b> {t("hub.desc.sender_sent")}</>
                : <>{t("hub.desc.you_were_sent")}</>}
              {info.last4 ? <> {t("hub.desc.ending")} <span className="mono font-semibold">{info.last4}</span></> : null}.
              {!allDone && ` ${t("hub.desc.complete_steps")}`}
            </p>

            {/* Progress */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-[#4A5C50] mb-1">
                <span data-testid="hub-progress-label">{t("hub.progress.label", { done: info.summary.done, total: info.summary.total })}</span>
                <span className="mono">{pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                <div
                  className={`h-full transition-all ${allDone ? "bg-emerald-500" : "bg-[#184A30]"}`}
                  style={{ width: `${pct}%` }}
                  data-testid="hub-progress-bar"
                />
              </div>
            </div>
          </div>

          {/* Required-blocker banner */}
          {blockerCount > 0 && (
            <div className="px-6 pt-4">
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-amber-900 text-sm flex items-start gap-2">
                <Warning size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">
                    {blockerCount === 1
                      ? t("hub.blocker.title_one")
                      : t("hub.blocker.title_many", { n: blockerCount })}
                  </p>
                  <p className="text-xs mt-0.5">{t("hub.blocker.subtext")}</p>
                </div>
              </div>
            </div>
          )}

          <ul className="px-6 py-5 space-y-3" data-testid="hub-steps">
            {info.steps.map((step) => <StepCard key={step.key} step={step} />)}
          </ul>
        </div>

        <p className="text-[11px] text-[#4A5C50] text-center mt-6">
          {t("hub.footer")}
        </p>
      </div>
    </div>
  );
}
